import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { DASHBOARD_CHARTS_LIMIT } from '~/common/constants/max_lengths';
import ManageFilters from '~/components/Shared/Filters/ManageFilters/ManageFilters';
import { OptionsType } from '~/components/Shared/Filters/ManageFilters/types';
import { StatusMessage } from '~/eds/src/components/StatusMessage/StatusMessage';
import { Text } from '~/eds/src/components/Text/Text';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { api } from '~/redux/api/createApi';
import { Dashboard } from '~/redux/api/methods/dashboardV2';
import { slices } from '~/redux/slices';
import dashboardV2, {
  convertFilterToChart,
  convertFilterTypeTochartType,
  getNonFilterCharts,
} from '~/redux/slices/dashboardV2/';
import { Chart, FiltersCount, SearchFilter } from '~/types/DashboardV2';
import { getValidEntity } from '~/utils/object';

interface Props {
  entity?: Dashboard['entity'];
}

const getSelectedChartsByGroup = (chartFilters: Chart[]) => {
  return chartFilters.reduce((acc, prev) => {
    const chartID = acc[prev.section as keyof typeof acc];
    return {
      ...acc,
      [prev.section]: chartID === undefined ? [prev.id] : [...chartID, prev.id],
    };
  }, {});
};

export const EditCharts = ({ entity }: Props) => {
  const intl = useIntl();
  const hasDeprecatePilotSearch = useFlag(FlagType.DeprecatePilotSearch);
  const shouldUseEvisearch =
    entity === 'ticket' || entity === 'workflow' || hasDeprecatePilotSearch;
  const { data: searchFilters } = api.endpoints[
    shouldUseEvisearch ? 'getFiltersV2' : 'getFilters'
  ].useQuery({
    type: entity,
  });
  const { data: sections } =
    api.endpoints.getFilterSections.useQuery(undefined);

  const groupsOrder = useMemo(
    () => sections?.fieldGroups.map((group) => group.id),
    [sections?.fieldGroups],
  );

  const chartFilters = useSelector(slices.dashboardV2.selectors.selectCharts);
  const activeDashboard = useSelector(
    slices.dashboardV2.selectors.selectActiveDashboard,
  );

  const nonFilterCharts: OptionsType[] = Object.values(
    getNonFilterCharts(activeDashboard),
  );

  let chartableFilters: OptionsType[] = Object.values(
    searchFilters || {},
  ).filter((field) => field.is_chartable);

  if (shouldUseEvisearch) {
    //Concatenate non filter charts if dashboard is pre sig.
    chartableFilters = chartableFilters.concat(nonFilterCharts);
  }

  const selectedCharts = getSelectedChartsByGroup(chartFilters);
  const getActionButtonTooltip = ({ selectedFiltersCount }: FiltersCount) => {
    if (selectedFiltersCount === 0) {
      return intl.formatMessage({
        id: 'EditCharts.selectChartTooltip',
        defaultMessage: 'Please select at least one chart.',
        description: 'Tooltip when no charts are selected',
      });
    } else if (selectedFiltersCount > DASHBOARD_CHARTS_LIMIT) {
      return intl.formatMessage(
        {
          id: 'EditCharts.chartLimitTooltip',
          defaultMessage:
            'The total number of charts selected cannot exceed {limit}.',
          description:
            'Tooltip when chart limit is exceeded. Example: limit = "100"',
        },
        { limit: DASHBOARD_CHARTS_LIMIT },
      );
    } else {
      return '';
    }
  };

  const renderLimitHeader = () => {
    return (
      <Text variant="body-bold">
        {intl.formatMessage(
          {
            id: 'EditCharts.selectFieldsHeader',
            defaultMessage: 'Select up to {limit} fields for your dashboard',
            description:
              'Header text for chart selection limit. Example: limit = "10"',
          },
          { limit: DASHBOARD_CHARTS_LIMIT },
        )}
      </Text>
    );
  };

  const renderHelpText = ({ selectedFiltersCount }: FiltersCount) => {
    if (selectedFiltersCount > DASHBOARD_CHARTS_LIMIT) {
      return (
        <StatusMessage
          status="info"
          message={intl.formatMessage(
            {
              id: 'EditCharts.maximumFieldsMessage',
              defaultMessage:
                'You have added the maximum number of fields ({limit}) to your dashboard.',
              description:
                'Message when maximum fields are added. Example: limit = "10"',
            },
            { limit: DASHBOARD_CHARTS_LIMIT },
          )}
        />
      );
    }
    return null;
  };

  const dispatch = useDispatch();

  const applyChartFilters = useCallback(
    (selectedFieldIds: string[]) => {
      const chartableFiltersMap = Object.fromEntries(
        chartableFilters.map((chart) => [chart.id, chart]),
      );
      const filterCharts: SearchFilter[] = getValidEntity(
        chartableFiltersMap,
        selectedFieldIds,
      );
      const charts = filterCharts.map((filterChart) => ({
        ...convertFilterToChart(filterChart),
        type:
          chartFilters.find((chart) => chart.id === filterChart.id)?.type ??
          convertFilterTypeTochartType(filterChart),
      }));
      dispatch(dashboardV2.actions.setChartFilters(charts));
      dispatch(dashboardV2.actions.setDashboardDirty(true));
    },
    [chartableFilters, chartFilters],
  );

  return (
    <ManageFilters
      preset="edit-charts"
      getActionButtonTooltip={getActionButtonTooltip}
      groupsOrder={groupsOrder || []}
      limit={DASHBOARD_CHARTS_LIMIT}
      renderHelpText={renderHelpText}
      renderLimitHeader={renderLimitHeader}
      searchFilters={chartableFilters}
      selectedFilters={selectedCharts}
      onApply={applyChartFilters}
    />
  );
};
